import { COMMON_FRAME_RATES, updateSettings, type ProjectSettings } from '@ve/core';
import { useState } from 'react';
import { Dialog } from '../../components/Dialog.tsx';
import { edit, useEditor } from '../../state/editor-store.ts';
import { tip } from '../../lib/tooltip.ts';
import { RESOLUTION_PRESETS } from '../project/ProjectPicker.tsx';

export function ProjectSettingsDialog({ onClose }: { onClose(): void }) {
  const current = useEditor((s) => s.project!.settings);
  const [draft, setDraft] = useState<ProjectSettings>(current);
  const fpsIndex = COMMON_FRAME_RATES.findIndex(
    (r) => r.fps.num === draft.fps.num && r.fps.den === draft.fps.den,
  );
  const valid = draft.width % 2 === 0 && draft.height % 2 === 0 && draft.width >= 16 && draft.height >= 16;

  const apply = () => {
    const ok = edit('Project settings', (p) => {
      updateSettings(p, draft);
      return true;
    });
    if (ok) onClose();
  };

  return (
    <Dialog
      title="Project settings"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} {...tip('Discard changes', 'Escape')}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!valid}
            onClick={apply}
            {...tip(
              valid ? 'Apply the new settings (undoable)' : 'Width and height must be even numbers ≥ 16',
            )}
          >
            Apply
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label>Preset</label>
        <select
          className="select"
          value=""
          onChange={(e) => {
            const p = RESOLUTION_PRESETS[Number(e.target.value)];
            if (p) setDraft({ ...draft, width: p.width, height: p.height });
          }}
        >
          <option value="">Choose a preset…</option>
          {RESOLUTION_PRESETS.map((p, i) => (
            <option key={p.label} value={i}>
              {p.label}
            </option>
          ))}
        </select>
        <label>Width × height</label>
        <div className="row">
          <input
            className="input"
            type="number"
            step={2}
            min={16}
            max={8192}
            value={draft.width}
            onChange={(e) => setDraft({ ...draft, width: Number(e.target.value) })}
          />
          ×
          <input
            className="input"
            type="number"
            step={2}
            min={16}
            max={8192}
            value={draft.height}
            onChange={(e) => setDraft({ ...draft, height: Number(e.target.value) })}
          />
          <span className="muted">px</span>
        </div>
        <label>Frame rate</label>
        <select
          className="select"
          value={fpsIndex}
          onChange={(e) => setDraft({ ...draft, fps: COMMON_FRAME_RATES[Number(e.target.value)]!.fps })}
        >
          {fpsIndex === -1 && <option value={-1}>{(draft.fps.num / draft.fps.den).toFixed(3)} fps</option>}
          {COMMON_FRAME_RATES.map((r, i) => (
            <option key={r.label} value={i}>
              {r.label} fps
            </option>
          ))}
        </select>
        <label>Background</label>
        <input
          type="color"
          className="color-input"
          value={draft.backgroundColor.slice(0, 7)}
          onChange={(e) => setDraft({ ...draft, backgroundColor: e.target.value })}
        />
        <label>Blending</label>
        <select
          className="select"
          value={draft.blending}
          onChange={(e) => setDraft({ ...draft, blending: e.target.value as ProjectSettings['blending'] })}
          {...tip('How overlapping layers, opacity and transitions mix colours')}
        >
          <option value="linear">Linear light (natural fades)</option>
          <option value="srgb">sRGB (classic)</option>
        </select>
      </div>
      {!valid && <p className="form-error">Width and height must be even numbers of at least 16 px.</p>}
      <p className="muted">
        Changing the frame rate re-times all clips so they keep their position in real time.
      </p>
    </Dialog>
  );
}
